// LWZO (docs/02-konzept.md E27): gemeinsame Bausteine der Benutzerpflege.
// Kopien aus Apps.tsx statt dortiger Exporte, damit die Upstream-Datei
// unberuehrt bleibt (docs/UPSTREAM.md, Divergenz-Strategie).
import { grpc } from "@improbable-eng/grpc-web";
import { mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { getErrorDetail } from "../api";
import { AuthContext } from "../generated/idp/api/idp";

// needsLogin: ein Unauthenticated vom IdP in die Anmeldemaske umsetzen.
//
// App.tsx haelt loggedIn optimistisch auf true; erst ein Aufruf von
// authenticate() zeigt den LoginFlow. Die Upstream-Admin-Seiten rufen ihn nur,
// wenn der Fehler einen AuthContext traegt - ohne Sitzung schickt der IdP aber
// ein nacktes "authentication required", und die Seite blieb mit dem Text
// stehen. account/Account.tsx loest das mit einem leeren AuthContext; hier
// dasselbe, damit ein Direktlink auf /admin/users/... zur Anmeldung fuehrt.
export function needsLogin(err: any, authenticate: (a: AuthContext) => void): boolean {
  if (err?.code !== grpc.Code.Unauthenticated) {
    return false;
  }
  const ia = getErrorDetail(err, AuthContext);
  authenticate(
    ia ?? AuthContext.fromPartial({ authenticationContextClass: [], maxAgeSeconds: 99999999 })
  );
  return true;
}

export const IconButton = styled.button`
  border: none;
  background: none;
  border-radius: 5px;
  color: #666;
  cursor: pointer;
  :hover {
    background: rgba(0, 0, 0, 0.15);
    color: black;
  }
  :active {
    background: rgba(0, 0, 0, 0.3);
  }
  :disabled {
    color: #bbb;
    cursor: default;
    background: none;
  }
`;

export const StripedTBody = styled.tbody`
  tr:nth-child(even) {
    background-color: #f2f2f2;
  }
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #e6e6e6;
  border-radius: 12px;
  padding: 2px 8px;
  margin: 2px;
  font-size: 13px;
  white-space: nowrap;
`;

export const SmallButton = styled.button`
  background-color: #e6e6e6;
  border: none;
  border-radius: 4px;
  padding: 4px 10px;
  margin: 2px;
  cursor: pointer;
  :hover {
    background-color: #c5c5c5;
  }
  :disabled {
    color: #999;
    cursor: default;
    background-color: #eee;
  }
`;

export const FieldLabel = styled.label`
  display: block;
  font-weight: bold;
  margin-top: 18px;
  margin-bottom: 4px;
`;

export const ErrorText = styled.div`
  font-weight: 600;
  color: #b84646;
  margin-top: 10px;
  white-space: pre-wrap;
`;

// Loeschknopf mit Rueckfrage: der erste Klick faerbt ihn rot, der zweite
// loescht, ein Klick daneben nimmt die Rueckfrage zurueck.
export function ConfirmingDeleteButton(props: {
  onDelete: () => void;
  title?: string;
  label?: string;
}): JSX.Element {
  const [isConfirming, setIsConfirming] = useState(false);
  const { onDelete } = props;
  const cb = useCallback(() => {
    if (!isConfirming) {
      setIsConfirming(true);
    } else {
      setIsConfirming(false);
      onDelete();
    }
  }, [isConfirming, onDelete]);
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsConfirming(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [ref]);
  return (
    <IconButton
      ref={ref}
      type="button"
      onClick={cb}
      title={isConfirming ? "Nochmals klicken zum Löschen" : props.title ?? "Löschen"}
      style={isConfirming ? { color: "white", backgroundColor: "#b84646" } : {}}
    >
      <Icon size={1} path={mdiTrashCanOutline} />
      {props.label ? <span style={{ marginLeft: 4 }}>{props.label}</span> : null}
    </IconButton>
  );
}

// Schluessel fuer React-Listen aus Byte-IDs (EmailAddress.id, TOTP.id).
export function bytesKey(id: Uint8Array | undefined): string {
  return Array.from(id ?? []).join(".");
}

export const collator = new Intl.Collator("de");
